import Head from 'next/head';
import dynamic from 'next/dynamic';
import { request } from 'graphql-request';
import BlockParser from '~/components/blockParser/blockParser';
import BlockVideos from '~/components/blockVideos/blockVideos';
import PageWrapper from '~/components/wrappers/pageWrapper';
import ParallaxHeader from '~/components/parallaxHeader/parallaxHeader';
import SiteSettings from '~/siteSettings';
import VideoBlock from '~/components/videoBlock/videoBlock';
import Share from '~/components/share/share';
import Skimlinks from '~/components/skimlinks/skimlinks';
import Category from '~/components/category/category';
import BodyParser from '~/components/bodyParser/bodyParser';
import TaxLinks from '~/components/taxLinks/taxLinks';
import {
  getBlocksFromQueue,
  getContentPaths,
  getAlias,
  has,
  get,
  getStructuredData,
  getDrupalSettings,
  getRecommendations,
  getDate
} from '~/utils/utils';

// GQL
import GET_EPISODE from '~/graphql/GET_EPISODE.graphql';
import GET_RELATED_EPISODE from '~/graphql/GET_RELATED_EPISODE.graphql';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });

/**
 * Episodes
 * @category Page
 * @extends Component
 * @summary Display the vertical page.
 * @return {Node} React node containing the episodes page.
 */

const Episode = props => {
  const { router, sitePrimaryRsid, baseUrl, fileUrl, data, blocks, relatedData, drupalSettings, recommendations } = props;
  const path = get(router, 'query.alias')
    ? `/episode/${ router.query.alias.join('/') }`
    : '/';

  const content = {
    ...data.nodeQuery
  };

  const keywords = content.field_tags && content.field_tags.map(tag => tag.name).join(',');

  return (
    <>
      <Head>
        <title>{ content.title } | { SiteSettings.siteName }</title>
        <meta name="title" content={ content.title } />
        <link rel='canonical' href={`${ baseUrl }${ path }`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ content.field_social_headline || content.title } />
        <meta name="description" content={ content.field_seo_description || content.field_subhead } />
        <meta property="og:url" content={ path } />
        <meta property="og:title" content={ content.field_social_headline || content.title } />
        <meta property="og:image" content={ content?.field_image?.field_image?.social?.url } />
        <meta property="og:description" content={ content.field_social_description || content.field_subhead } />
        <meta property="article:published_time" content={ content.field_display_date } />
        <meta name="keywords" content={ keywords || SiteSettings.defaultKeywords } />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, path, content)) }}
        />
      </Head>
      {/* Main Image */}
      {!content.field_video &&
        <>
          <ParallaxHeader>
            <div className='content__header-img'>
              { content?.field_image?.field_image &&
                <picture>
                  <source media='(max-width: 375px)' srcSet={ content.field_image.field_image.sm.url } />
                  <img
                    src={ content.field_image.field_image.lg.url }
                    alt={ content.field_image?.alt }
                    width={ content.field_image.field_image.lg.width }
                    height={ content.field_image.field_image.lg.height }
                    className='img-full'
                    loading='lazy'
                  />
                </picture>
              }
              { !content?.field_image?.field_image &&
                <div className='img-missing'>No Image Available</div>
              }
            </div>
          </ParallaxHeader>
          <div className='content site-bg'>
            <br />
            <div className='content__body'>
              <div className='content__share desktop-only'>
                <Share title={ content.title } />
              </div>
              {/* Title */}
              <h1 className='content__title-sm'>
                { content.title }
              </h1>
              {/* Is Promotional Flag */}
              { content.field_is_promotional && content.field_flag &&
                <div className='content__flag'>
                  { content.field_flag }
                </div>
              }
              <Category content={content} />
              {/* Byline */}
              { content.field_display_date &&
                <div className='content__byline'>
                  <div className='content__byline-date'>
                    { getDate(content.field_display_date) }
                  </div>
                </div>
              }
              {/* Skimlinks */}
              { content.field_skimlinks &&
                <Skimlinks
                  type={ content.__typename }
                  data={ drupalSettings }
                />
              }
              {/* Subhead */}
              { content.field_subhead &&
                <h2>
                  { content.field_subhead }
                </h2>
              }
              {/* Body */}
              { content.body &&
                <div className='content__body-main content__body-main--larger'>
                  <BodyParser
                    baseDomain={ fileUrl }
                    content={ content.body }
                    drupalSettings={ drupalSettings }
                  />
                </div>
              }
              <div className='content__spacer'></div>
              <TaxLinks content={ content } />
              <div data-ad-ref='leader-plus-flex-top' className='bidbarrel-ad'></div>
              <div data-ad-ref='mobile-plus-flex-top' className='bidbarrel-ad'></div>
            </div>
          </div>
        </>
      }
      {/* Main Video */}
      {content.field_video && has(content, 'field_video.field_video_mpx_id.hls_streaming_url') &&
        <VideoBlock
          data={ data }
          parentNid={ content.id }
          pageType='episode'
          section='episode'
          fileUrl={ fileUrl }
          drupalSettings={ drupalSettings }
          recommendations={ recommendations }
        />
      }
      <div className='site-bg'>
        <div className='constrain'>
          <div data-ad-ref='leader-plus-flex-top' className='bidbarrel-ad'></div>
          <div data-ad-ref='mobile-plus-flex-top' className='bidbarrel-ad'></div>
        </div>
      </div>
      <BlockVideos
        seeMore
        seeMoreAt={2}
        items={ relatedData?.viewQuery?.resultRow }
      />
      <div data-ad-ref='leader-plus-flex-middle' className='bidbarrel-ad'></div>
      <div data-ad-ref='mobile-plus-flex-middle' className='bidbarrel-ad'></div>
      { blocks?.entitySubQueueQuery?.items &&
        <BlockParser
          blockItems={ blocks.entitySubQueueQuery.items }
        />
      }
      <div data-ad-ref='leader-plus-flex-bottom' className='bidbarrel-ad'></div>
      <div data-ad-ref='mobile-plus-flex-bottom' className='bidbarrel-ad'></div>
      <Utag pageType='episode' siteSection='show info' data={{
        sitePrimaryRsid,
        ...(keywords && {keywords})
      }} />
      <BidBarrelReact
        data={ content }
        mapToAdUnits={{
          mobile: [
            'mobile-omni-skybox-plus-sticky',
            'mobile-plus-flex-top',
            'mobile-plus-flex-middle',
            'mobile-plus-flex-bottom'
          ],
          desktop: [
            'omni-skybox-plus-sticky',
            'leader-plus-flex-top',
            'leader-plus-flex-middle',
            'leader-plus-flex-inc',
            'leader-plus-flex-bottom'
          ]
        }}
      />
    </>
  );
}

export async function getStaticPaths() {
  const paths = await getContentPaths('/episode/', {
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'episodes'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  const aliasData = await getAlias({
    alias: `/episode/${ ctx.params.alias.join('/') }`
  });

  if(!aliasData?.pathAliasQuery?.nid) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Data
  const data = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_EPISODE, {
    target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
  });

  if(!data?.nodeQuery) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Related Items
  const relatedData = [];
  if(data?.nodeQuery?.field_episode?.tid) {
    const relatedData = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_RELATED_EPISODE, {
      machine_name: 'episode_related',
      display: 'master',
      itemsPerPage: 20,
      offset: 0,
      viewArgs: [data.nodeQuery.field_episode.tid.toString()]
    });
  }

  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'episodes',
    limit: 100
  });

  // Get Site Settings
  const drupalSettings = await getDrupalSettings();

  // Get Recomendations
  const recommendations = await getRecommendations();

  return {
    props: {
      data,
      blocks,
      relatedData,
      drupalSettings,
      recommendations
    },
    revalidate: 1
  }
}

export default PageWrapper(Episode);